"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import EditableElement from "@/components/customize/EditableElement";

export default function Gallery({
  heading,
  subtitle,
  images = [],
  interval = 5000,
  templateMeta,
  __isMobile = false,
  __isEditable = false,
  __sectionId,
  __onFieldEdit,
  __onFieldChange,
  __focusFieldName,
}) {
  const safeImages = (images || []).filter(
    (img) => typeof img === "string" && img.trim() !== ""
  );
  const [current, setCurrent] = useState(0);
  const safeCurrent = safeImages.length ? current % safeImages.length : 0;
  
  const coupleName = `${templateMeta?.groomFirstName || "Groom"} & ${templateMeta?.brideFirstName || "Bride"}`;

  const handlePrev = () => {
    setCurrent(current === 0 ? images.length - 1 : current - 1);
  };

  const handleNext = () => {
    setCurrent((current + 1) % images.length);
  };

  useEffect(() => {
    if (!safeImages.length) return;
    const autoSlide = setInterval(() => {
      setCurrent((prev) => (prev + 1) % safeImages.length);
    }, interval);
    return () => clearInterval(autoSlide);
  }, [safeImages.length, interval]);

  if (!safeImages.length) return null;
  const titleClass = __isMobile ? "text-3xl" : "text-3xl md:text-4xl";

  return (
    <div className="relative px-5 py-6">
      <div className="relative z-10">
        {heading && (
          <EditableElement
            type="text"
            value={heading}
            onChange={(val) => __onFieldChange?.(__sectionId, "heading", val)}
            isEditable={__isEditable}
            fieldName="heading"
            onEditStart={() => __onFieldEdit?.(__sectionId, "heading")}
            isActive={__focusFieldName === "heading"}
            isMobile={__isMobile}
          >
            <p className={`font-cormorant font-semibold text-center text-white ${titleClass}`}>
              {heading}
            </p>
          </EditableElement>
        )}
        {subtitle && (
          <EditableElement
            type="text"
            value={subtitle}
            onChange={(val) => __onFieldChange?.(__sectionId, "subtitle", val)}
            isEditable={__isEditable}
            fieldName="subtitle"
            onEditStart={() => __onFieldEdit?.(__sectionId, "subtitle")}
            isActive={__focusFieldName === "subtitle"}
            isMobile={__isMobile}
          >
            <p className="text-xs text-center text-white font-sans">{subtitle}</p>
          </EditableElement>
        )}

        {/* Slider Section */}
        <div className={`relative my-6 mx-auto overflow-hidden h-100 ${__isMobile ? "w-full" : "max-w-6xl w-full"}`}>
          <AnimatePresence mode="wait">
            <motion.div
              key={safeCurrent}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8 }}
              className="absolute inset-0 h-full w-full"
              style={{ willChange: "opacity" }}
            >
              <div className="relative w-full h-full">
                <Image
                  src={safeImages[safeCurrent]}
                  alt="gallery"
                  fill
                  className="object-cover"
                  style={{ objectPosition: "center" }}
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  priority={safeCurrent === 0}
                />
                {coupleName && (
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
                    <p className="font-caudex text-sm text-white tracking-widest px-3 py-1 rounded-full bg-black/20 backdrop-blur-sm whitespace-nowrap">
                      {coupleName}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <button
            onClick={handlePrev}
            aria-label="Previous image"
            className="absolute top-1/2 left-4 -translate-y-1/2 text-white bg-black/30 p-2 rounded-full hover:bg-black/50 transition"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={handleNext}
            aria-label="Next image"
            className="absolute top-1/2 right-4 -translate-y-1/2 text-white bg-black/30 p-2 rounded-full hover:bg-black/50 transition"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  );
}
